import React from 'react'
import numeral from 'numeral'
import { get } from 'lodash'
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts'
import { Tabs, Tab } from '@blueprintjs/core'
import { ICostBuilding } from '@api/cost/types'
import { Chart } from '@ui'
import track from '@track'
import { tabs, ITab } from '.'
import { ICost } from './hooks/useCost'
import css from './chart.module.scss'

export default function ChartContainer({
  buildings,
  cost,
  tabId,
  setTabId,
}: {
  buildings: ICostBuilding[]
  cost: ICost
  tabId: string
  setTabId: (id: string) => void
}) {
  return (
    <div className="cost">
      <Tabs
        onChange={(id: string) => {
          setTabId(id)
          const tab = tabs.find((t) => t.dataKey === id) as ITab
          cost.setSort(tab.dataKey)
          track.cost({
            eventName: track.Event.TAB_CLICK,
            props: {
              tabName: tab.title,
            },
          })
        }}
        selectedTabId={tabId}
      >
        {tabs.map((t) => (
          <Tab
            key={t.dataKey}
            id={t.dataKey}
            title={t.render(cost)}
            panel={<CostChart {...{ buildings, tab: t }} />}
          />
        ))}
      </Tabs>
    </div>
  )
}

interface ChartProps {
  buildings: ICostBuilding[]
  tab: ITab
}
function CostChart({ tab, buildings }: ChartProps) {
  return (
    <ResponsiveContainer height={200} width="100%">
      <BarChart data={buildings} className="m0">
        <defs>
          <linearGradient id="fill" gradientTransform="rotate(90)">
            <stop offset="0" stopColor="#128DEE" />
            <stop offset="1" stopColor="#24C7A3" />
          </linearGradient>
        </defs>
        <Tooltip animationDuration={0} content={<CustomTooltip tab={tab} />} />
        <CartesianGrid strokeDasharray="1 1" vertical={false} />
        <YAxis
          tickFormatter={(tick) => numeral(tick).format('($ 0.0a)')}
          tickSize={4}
          width={100}
          axisLine={false}
          label={{
            value: tab.title,
            position: 'insideLeft',
          }}
        />
        <XAxis dataKey="name" />
        <Bar
          barSize={30}
          isAnimationActive={false}
          maxBarSize={15}
          dataKey={tab.dataKey}
          name={tab.title}
          unit="$"
          stackId="a"
          fill="url(#fill)"
        />
      </BarChart>
    </ResponsiveContainer>
  )
}

function CustomTooltip({ active, payload, tab }: TooltipProps & { tab: ITab }) {
  if (!active || !payload) return null
  const building = payload[0].payload

  return (
    <Chart.Tooltip>
      <div className={css.info}>
        <h1>{building.name}</h1>
        <h3>
          {tab.title}: <b>{numeral(get(building, tab.dataKey)).format(tab.format)}</b>
        </h3>
      </div>
    </Chart.Tooltip>
  )
}
